import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Edit,
  Trash2,
  Eye,
  Search,
  X,
  BookOpen,
  Tag,
  Calendar,
  BarChart3,
  Target,
  Clock,
  FileText,
  Image,
  Layers,
  Filter,
  RefreshCw,
} from "lucide-react";
import { Pagination } from "@mui/material";
import { server } from "../server";

export default function QuestionsList() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDifficulty, setFilterDifficulty] = useState("all");
  const [filterSubject, setFilterSubject] = useState("");
  const [filterChapter, setFilterChapter] = useState("");
  const [filterTopic, setFilterTopic] = useState("");
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [modalType, setModalType] = useState(""); // "view" or "edit"
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [topics, setTopics] = useState([]);

  const itemsPerPage = 12;

  // Fetch dropdown data
  const fetchDropdownData = async () => {
    try {
      // Fetch subjects
      const subjectsRes = await axios.get(`${server}/subject/all`, {
        params: { pageNo: 1, pageSize: 100 },
      });
      setSubjects(subjectsRes.data?.data || []);

      // Fetch chapters
      const chaptersRes = await axios.get(`${server}/chapter/all`, {
        params: { pageNo: 1, pageSize: 100 },
      });
      setChapters(chaptersRes.data?.data || []);

      // Fetch topics
      const topicsRes = await axios.get(`${server}/topics/all`, {
        params: { pageNo: 1, pageSize: 100 },
      });
      setTopics(topicsRes.data?.data || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  const fetchQuestions = async (page = 1) => {
    try {
      setLoading(true);

      const params = {
        pageNo: page,
        pageSize: itemsPerPage,
      };

      // Add search and filter params
      if (searchTerm) params.searchParams = searchTerm;
      if (filterSubject) params.subjectId = filterSubject;
      if (filterChapter) params.chapterId = filterChapter;
      if (filterTopic) params.topicId = filterTopic;
      if (filterDifficulty !== "all") params.difficulty = filterDifficulty;
      if (filterStatus !== "all") params.status = filterStatus;

      const { data } = await axios.get(`${server}/question/all`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });

      setQuestions(data?.data || []);
      setTotalCount(data?.count || 0);
      setCurrentPage(page);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to fetch questions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDropdownData();
    fetchQuestions(1);
  }, []);

  // Fetch questions when filters change
  useEffect(() => {
    fetchQuestions(1);
  }, [
    searchTerm,
    filterStatus,
    filterDifficulty,
    filterSubject,
    filterChapter,
    filterTopic,
  ]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this question?")) return;
    try {
      await axios.delete(`${server}/questions/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      fetchQuestions(currentPage); // Refresh current page
    } catch {
      alert("Delete failed");
    }
  };

  const handleToggleActive = async (id, isActive) => {
    try {
      await axios.put(
        `${server}/questions/${id}`,
        { isActive: !isActive },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      fetchQuestions(currentPage); // Refresh current page
    } catch {
      alert("Failed to toggle status");
    }
  };

  const handleModalOpen = (question, type) => {
    setSelectedQuestion(question);
    setModalType(type);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${server}/questions/${selectedQuestion.id}`,
        selectedQuestion,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      fetchQuestions(currentPage);
      setModalType("");
    } catch {
      alert("Failed to update question");
    }
  };

  const handlePageChange = (event, value) => {
    fetchQuestions(value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setFilterDifficulty("all");
    setFilterSubject("");
    setFilterChapter("");
    setFilterTopic("");
  };

  const difficultyColors = {
    easy: "bg-green-500/20 text-green-400 border-green-500/30",
    medium: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    hard: "bg-red-500/20 text-red-400 border-red-500/30",
    string: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };

  const statusColors = {
    approved: "bg-green-500/20 text-green-400 border-green-500/30",
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    rejected: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  const getSubjectName = (subjectId) => {
    const subject = subjects.find((s) => s.id === subjectId);
    return subject ? subject.subject : "Unknown Subject";
  };

  const getChapterName = (chapterId) => {
    const chapter = chapters.find((c) => c.id === chapterId);
    return chapter ? chapter.chapter : "Unknown Chapter";
  };

  const getTopicName = (topicId) => {
    const topic = topics.find((t) => t.id === topicId);
    return topic ? topic.name : "Unknown Topic";
  };

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  return (
    <div className="min-h-screen bg-gray-900 p-6 relative">
      {/* Header with Search and Filters */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Questions List</h1>
          <p className="text-gray-400 text-sm mt-1">
            Total: {totalCount} questions
          </p>
        </div>
        <div className="flex items-center gap-3">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={clearFilters}
            className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Clear Filters
          </motion.button>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Search Questions
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search questions..."
                className="pl-9 pr-3 py-2 border border-gray-600 rounded-lg w-full text-sm bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Subject Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Subject
            </label>
            <select
              className="w-full p-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value)}
            >
              <option value="">All Subjects</option>
              {subjects.map((subject) => (
                <option key={subject.id} value={subject.id}>
                  {subject.icon} {subject.subject}
                </option>
              ))}
            </select>
          </div>

          {/* Chapter Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Chapter
            </label>
            <select
              className="w-full p-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={filterChapter}
              onChange={(e) => setFilterChapter(e.target.value)}
            >
              <option value="">All Chapters</option>
              {chapters.map((chapter) => (
                <option key={chapter.id} value={chapter.id}>
                  {chapter.chapter}
                </option>
              ))}
            </select>
          </div>

          {/* Topic Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Topic
            </label>
            <select
              className="w-full p-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={filterTopic}
              onChange={(e) => setFilterTopic(e.target.value)}
            >
              <option value="">All Topics</option>
              {topics.map((topic) => (
                <option key={topic.id} value={topic.id}>
                  {topic.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Difficulty Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Difficulty
            </label>
            <select
              className="w-full p-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
            >
              <option value="all">All Difficulty</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Status
            </label>
            <select
              className="w-full p-2 border border-gray-600 rounded-lg text-sm bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="approved">Approved</option>
              <option value="pending">Pending</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {/* Active Questions Count */}
          <div className="flex items-center justify-center">
            <div className="text-center p-3 bg-gray-700 rounded-lg">
              <div className="text-2xl font-bold text-white">{totalCount}</div>
              <div className="text-xs text-gray-400">Total Questions</div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-400">Loading questions...</span>
        </div>
      ) : questions.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">No questions found</div>
          <div className="text-gray-500 text-sm">
            {searchTerm ||
            filterStatus !== "all" ||
            filterDifficulty !== "all" ||
            filterSubject ||
            filterChapter ||
            filterTopic
              ? "Try adjusting your search or filters"
              : "No questions available in the system"}
          </div>
        </div>
      ) : (
        <>
          {/* Questions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 gap-6 mb-8">
            <AnimatePresence>
              {questions.map((q) => (
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="bg-gray-800 border border-gray-700 rounded-xl p-5 hover:border-gray-600 transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/10"
                >
                  {/* Header with Actions */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1 pr-4">
                      <h2 className="font-semibold text-white text-lg leading-tight line-clamp-2 mb-2">
                        {q.question}
                      </h2>
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`px-2 py-1 text-xs rounded-full border ${
                            difficultyColors[q.difficulty] ||
                            difficultyColors.string
                          }`}
                        >
                          {q.difficulty || "Not set"}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs rounded-full border ${
                            statusColors[q.status] || statusColors.pending
                          }`}
                        >
                          {q.status}
                        </span>
                        {!q.isActive && (
                          <span className="px-2 py-1 text-xs rounded-full bg-gray-500/20 text-gray-400 border border-gray-500/30">
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleModalOpen(q, "view")}
                        className="text-gray-400 hover:text-blue-400 transition-colors p-1"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleModalOpen(q, "edit")}
                        className="text-gray-400 hover:text-green-400 transition-colors p-1"
                        title="Edit Question"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(q.id)}
                        className="text-gray-400 hover:text-red-400 transition-colors p-1"
                        title="Delete Question"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Answer Preview */}
                  <p className="text-gray-300 text-sm mb-4 line-clamp-2">
                    <strong>Answer:</strong> {q.answer}
                  </p>

                  {/* Options */}
                  {q.options && q.options.length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs text-gray-400 mb-2">Options:</div>
                      <div className="flex flex-wrap gap-1">
                        {q.options.map((option, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded border border-gray-600"
                          >
                            {option}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Metadata */}
                  <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                    <span className="flex items-center gap-1 text-blue-400">
                      <BookOpen size={12} /> {q.subject || "No subject"}
                    </span>
                    <span className="flex items-center gap-1 text-green-400">
                      <Tag size={12} /> {q.topic || "No topic"}
                    </span>
                    <span className="flex items-center gap-1 text-purple-400">
                      <Layers size={12} /> {q.subTopic || "No subtopic"}
                    </span>
                    <span className="flex items-center gap-1 text-yellow-400">
                      <Calendar size={12} /> {q.year || "No year"}
                    </span>
                  </div>

                  {/* Tags */}
                  {q.tags && q.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {q.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded border border-gray-600"
                        >
                          {tag}
                        </span>
                      ))}
                      {q.tags.length > 3 && (
                        <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded border border-gray-600">
                          +{q.tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Stats and Actions */}
                  <div className="flex justify-between items-center pt-3 border-t border-gray-700">
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <BarChart3 size={12} />
                        {q.accuracyRate !== null ? `${q.accuracyRate}%` : "N/A"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Target size={12} />
                        {q.timesCorrect !== null ? q.timesCorrect : "0"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={12} />
                        {q.averageTimeSpent !== null
                          ? `${q.averageTimeSpent}s`
                          : "N/A"}
                      </span>
                    </div>
                    <button
                      onClick={() => handleToggleActive(q.id, q.isActive)}
                      className={`text-sm font-medium px-3 py-1 rounded-lg transition-colors border ${
                        q.isActive
                          ? "bg-red-500/20 text-red-400 border-red-500/30 hover:bg-red-500/30"
                          : "bg-green-500/20 text-green-400 border-green-500/30 hover:bg-green-500/30"
                      }`}
                    >
                      {q.isActive ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
                sx={{
                  "& .MuiPaginationItem-root": {
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: "#E5E7EB",
                    "&.Mui-selected": {
                      background: "linear-gradient(135deg, #3B82F6, #6366F1)",
                      color: "white",
                      "&:hover": {
                        background: "linear-gradient(135deg, #2563EB, #4F46E5)",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                  },
                }}
              />
            </div>
          )}
        </>
      )}

      {/* View/Edit Modal */}
      <AnimatePresence>
        {(modalType === "view" || modalType === "edit") && selectedQuestion && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute top-4 left-4 right-4 rounded-3xl h-full w-auto mx-auto bg-black bg-opacity-60 flex justify-center items-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-4xl max-h-[100vh] overflow-hidden"
            >
              {/* Modal Header */}
              <div className="flex justify-between items-center p-6 border-b border-gray-700">
                <h3 className="text-xl font-semibold text-white">
                  {modalType === "view" ? "Question Details" : "Edit Question"}
                </h3>
                <button
                  onClick={() => setModalType("")}
                  className="text-gray-400 hover:text-white transition-colors p-1"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                {modalType === "view" ? (
                  /* View Mode */
                  <div className="space-y-6">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Difficulty
                        </label>
                        <div className="p-3 bg-gray-700 rounded-lg text-white">
                          {selectedQuestion.difficulty || "Not set"}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Status
                        </label>
                        <div className="p-3 bg-gray-700 rounded-lg text-white">
                          {selectedQuestion.status}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Question
                      </label>
                      <div className="p-3 bg-gray-700 rounded-lg text-white whitespace-pre-wrap">
                        {selectedQuestion.question}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Answer
                      </label>
                      <div className="p-3 bg-gray-700 rounded-lg text-white">
                        {selectedQuestion.answer}
                      </div>
                    </div>

                    {/* Options */}
                    {selectedQuestion.options &&
                      selectedQuestion.options.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Options
                          </label>
                          <div className="space-y-2">
                            {selectedQuestion.options.map((option, index) => (
                              <div
                                key={index}
                                className="p-3 bg-gray-700 rounded-lg text-white"
                              >
                                {option}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                    {/* Additional Fields */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Subject
                        </label>
                        <div className="p-3 bg-gray-700 rounded-lg text-white">
                          {selectedQuestion.subject}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Topic
                        </label>
                        <div className="p-3 bg-gray-700 rounded-lg text-white">
                          {selectedQuestion.topic}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Sub Topic
                        </label>
                        <div className="p-3 bg-gray-700 rounded-lg text-white">
                          {selectedQuestion.subTopic}
                        </div>
                      </div>
                    </div>

                    {/* URLs */}
                    <div className="grid grid-cols-3 gap-4">
                      {selectedQuestion.imageUrl &&
                        selectedQuestion.imageUrl !== "string" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Image
                            </label>
                            <div className="p-3 bg-gray-700 rounded-lg">
                              <img
                                src={selectedQuestion.imageUrl}
                                alt="Question"
                                className="max-w-full h-auto rounded"
                              />
                            </div>
                          </div>
                        )}
                      {selectedQuestion.diagramUrl &&
                        selectedQuestion.diagramUrl !== "string" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Diagram
                            </label>
                            <div className="p-3 bg-gray-700 rounded-lg">
                              <img
                                src={selectedQuestion.diagramUrl}
                                alt="Diagram"
                                className="max-w-full h-auto rounded"
                              />
                            </div>
                          </div>
                        )}
                      {selectedQuestion.formulaUrl &&
                        selectedQuestion.formulaUrl !== "string" && (
                          <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                              Formula
                            </label>
                            <div className="p-3 bg-gray-700 rounded-lg">
                              <img
                                src={selectedQuestion.formulaUrl}
                                alt="Formula"
                                className="max-w-full h-auto rounded"
                              />
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  /* Edit Mode */
                  <form onSubmit={handleUpdate} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Question
                        </label>
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          value={selectedQuestion.question}
                          onChange={(e) =>
                            setSelectedQuestion({
                              ...selectedQuestion,
                              question: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Answer
                        </label>
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          value={selectedQuestion.answer}
                          onChange={(e) =>
                            setSelectedQuestion({
                              ...selectedQuestion,
                              answer: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Subject
                        </label>
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          value={selectedQuestion.subject}
                          onChange={(e) =>
                            setSelectedQuestion({
                              ...selectedQuestion,
                              subject: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Topic
                        </label>
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          value={selectedQuestion.topic}
                          onChange={(e) =>
                            setSelectedQuestion({
                              ...selectedQuestion,
                              topic: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Sub Topic
                        </label>
                        <input
                          type="text"
                          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          value={selectedQuestion.subTopic}
                          onChange={(e) =>
                            setSelectedQuestion({
                              ...selectedQuestion,
                              subTopic: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Difficulty
                        </label>
                        <select
                          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          value={selectedQuestion.difficulty}
                          onChange={(e) =>
                            setSelectedQuestion({
                              ...selectedQuestion,
                              difficulty: e.target.value,
                            })
                          }
                        >
                          <option value="easy">Easy</option>
                          <option value="medium">Medium</option>
                          <option value="hard">Hard</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Year
                        </label>
                        <input
                          type="number"
                          className="w-full p-3 border border-gray-600 rounded-lg bg-gray-700 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          value={selectedQuestion.year}
                          onChange={(e) =>
                            setSelectedQuestion({
                              ...selectedQuestion,
                              year: parseInt(e.target.value),
                            })
                          }
                        />
                      </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                      >
                        Save Changes
                      </button>
                      <button
                        type="button"
                        onClick={() => setModalType("")}
                        className="flex-1 bg-gray-600 text-white py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
